import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { produksjonAPI } from '../services/api/produksjon.api';
import { queryKeys } from '../lib/queryKeys';

/**
 * React Query hook for oppmøtetider
 * 
 * Gir tilgang til oppmøtetider for en produksjon med:
 * - Automatisk caching
 * - Background refetching
 * - Optimistic updates ved endringer
 * - Bedre feilhåndtering
 * 
 * @param produksjonId - Produksjon ID
 */
export const useOppmoteData = (produksjonId: number | undefined) => {
  const queryClient = useQueryClient();

  // Query: Hent oppmøtetider
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.productions.oppmote(produksjonId!),
    queryFn: () => produksjonAPI.getOppmote(produksjonId!).then(res => res.oppmote),
    enabled: !!produksjonId,
  });

  const oppmote = data || [];

  // Mutation: Legg til oppmøtetid
  const addOppmote = useMutation({
    mutationFn: (data: {
      navn: string;
      beskrivelse?: string;
      tidspunkt?: string;
      minutterFør?: number;
      rekkefølge?: number;
    }) => produksjonAPI.addOppmote(produksjonId!, data),
    onSuccess: () => {
      // Invalider oppmøtetider for å hente oppdatert data
      queryClient.invalidateQueries({
        queryKey: queryKeys.productions.oppmote(produksjonId!),
      });
    },
  });

  // Mutation: Oppdater oppmøtetid
  const updateOppmote = useMutation({
    mutationFn: ({ oppmoteId, data }: {
      oppmoteId: number;
      data: {
        navn?: string;
        beskrivelse?: string;
        tidspunkt?: string;
        minutterFør?: number;
        rekkefølge?: number;
      };
    }) => produksjonAPI.updateOppmote(produksjonId!, oppmoteId, data),
    onSuccess: () => {
      // Invalider oppmøtetider for å hente oppdatert data
      queryClient.invalidateQueries({
        queryKey: queryKeys.productions.oppmote(produksjonId!),
      });
    },
  });

  // Mutation: Slett oppmøtetid
  const removeOppmote = useMutation({
    mutationFn: (oppmoteId: number) => produksjonAPI.removeOppmote(produksjonId!, oppmoteId),
    onSuccess: () => {
      // Invalider oppmøtetider for å hente oppdatert data
      queryClient.invalidateQueries({
        queryKey: queryKeys.productions.oppmote(produksjonId!),
      });
    },
  });

  // Mutation: Legg til talent til oppmøtetid
  const addTalentToOppmote = useMutation({
    mutationFn: ({ oppmoteId, data }: {
      oppmoteId: number;
      data: {
        talentNavn: string;
        talentKategoriSti?: string;
        erGruppe?: boolean;
      };
    }) => produksjonAPI.addTalentToOppmote(produksjonId!, oppmoteId, data),
    onSuccess: () => {
      // Invalider oppmøtetider for å hente oppdatert data
      queryClient.invalidateQueries({
        queryKey: queryKeys.productions.oppmote(produksjonId!),
      });
    },
  });

  // Mutation: Fjern talent fra oppmøtetid
  const removeTalentFromOppmote = useMutation({
    mutationFn: ({ oppmoteId, talentNavn, talentKategoriSti }: {
      oppmoteId: number;
      talentNavn: string;
      talentKategoriSti?: string;
    }) => produksjonAPI.removeTalentFromOppmote(produksjonId!, oppmoteId, talentNavn, talentKategoriSti),
    onSuccess: () => {
      // Invalider oppmøtetider for å hente oppdatert data
      queryClient.invalidateQueries({
        queryKey: queryKeys.productions.oppmote(produksjonId!),
      });
    },
  });

  return {
    oppmote,
    loading: isLoading,
    error,
    refresh: refetch,
    
    // Mutations
    addOppmote: addOppmote.mutateAsync,
    updateOppmote: updateOppmote.mutateAsync,
    removeOppmote: removeOppmote.mutateAsync,
    addTalentToOppmote: addTalentToOppmote.mutateAsync,
    removeTalentFromOppmote: removeTalentFromOppmote.mutateAsync,
    
    // Mutation states
    isAdding: addOppmote.isPending,
    isUpdating: updateOppmote.isPending,
    isRemoving: removeOppmote.isPending,
    isAddingTalent: addTalentToOppmote.isPending,
    isRemovingTalent: removeTalentFromOppmote.isPending,
  };
};

